'use client';

import { useMemo, useState } from 'react';
import {
  DEFAULT_LOADER_STATE,
  LOADER_STYLES,
  LOADER_STYLE_LABELS,
  loaderCss,
  loaderHtml,
  loaderSnippet,
  type LoaderState,
  type LoaderStyle,
} from '@/lib/browser-cssanim-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

const PREVIEW_PREFIX = 'cleanor-loader-preview';

export default function BrowserCssLoaderGenerator() {
  const [state, setState] = useState<LoaderState>(DEFAULT_LOADER_STATE);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const output = useMemo(() => loaderSnippet(state), [state]);
  const previewCss = useMemo(() => loaderCss(state, PREVIEW_PREFIX), [state]);
  const previewHtml = useMemo(() => loaderHtml(state, PREVIEW_PREFIX), [state]);

  function update<K extends keyof LoaderState>(key: K, value: LoaderState[K]) {
    setState((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCopy() {
    try {
      await copyText(output);
      setCopyMessage('Copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the value.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Loader</span>
        <h2 className="tool-live-title">CSS loader generator</h2>
        <p className="tool-live-subtitle">
          Build a pure-CSS spinner: pick a style, color, size, and speed, preview it live, and copy
          the HTML + CSS.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field">
              <span>Style</span>
              <select
                onChange={(e) => update('style', e.target.value as LoaderStyle)}
                value={state.style}
              >
                {LOADER_STYLES.map((style) => (
                  <option key={style} value={style}>
                    {LOADER_STYLE_LABELS[style]}
                  </option>
                ))}
              </select>
            </label>
            <label className="tool-live-field">
              <span>Color</span>
              <input
                onChange={(e) => update('color', e.target.value)}
                type="color"
                value={state.color}
              />
            </label>
            <label className="tool-live-field">
              <span>Size: {state.size}px</span>
              <input
                max={96}
                min={16}
                onChange={(e) => update('size', Number(e.target.value))}
                type="range"
                value={state.size}
              />
            </label>
            <label className="tool-live-field">
              <span>Speed: {state.speed}s</span>
              <input
                max={3}
                min={0.3}
                onChange={(e) => update('speed', Number(e.target.value))}
                step={0.1}
                type="range"
                value={state.speed}
              />
            </label>
          </div>

          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy result
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            className="tool-live-preview-frame"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
            }}
          >
            <style>{previewCss}</style>
            <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>

          <label className="tool-live-field tool-live-field-full">
            <span>HTML + CSS</span>
            <textarea readOnly rows={14} value={output} />
          </label>
          <p className="tool-live-panel-note">Generated locally in your browser.</p>
        </section>
      </div>
    </div>
  );
}
